import {action, computed, observable} from 'mobx';
import {IDomUtilsService} from '@techsee/techsee-client-infra/lib/services/DomUtilsService';
import {resolveLogoUrl} from '../../utils/logo-url.utils';
import {ITranslate} from '../../services/LocalizationService';
import {getRootStore} from '../../app.bootstrap';
import {AccountAssetsType} from '@techsee/techsee-client-infra/lib/services/AccountBrandingService';

const LOGO_URL_ASSET_NAME = 'logoUrl';

export enum InvitationEndedSessionTypes {
    regular = '0',
    warmTransfer = '1'
}

export enum InvitationEndedReason {
    sessionExpired = 'sessionExpired',
    meetingNotAvailable = 'meetingNotAvailable',
    guestLimitExceeded = 'guestLimitExceeded',
    removedByAgent = 'removedByAgent',
    requestDenied = 'requestDenied'
}

export interface IInvitationEndedController {
    readonly translate: ITranslate;
    readonly _domUtilsService: IDomUtilsService;
    readonly isSessionEnded: boolean;
    readonly isExpired: boolean;
    readonly sessionEndedMessage: string;
    readonly wtSessionEndedMessages?: string[];
    readonly isPhone: boolean;
    readonly sessionType: InvitationEndedSessionTypes;
    readonly errorMessage?: string;
    readonly logoImage: string;
    readonly warmTransferExpired: boolean;

    loadImage(img: string): string;
    hasBrandingLogo(): boolean;
    goToInvitePage: () => void;
}

export class InvitationEndedController implements IInvitationEndedController {
    private readonly _translate: ITranslate;

    readonly isSessionEnded: boolean;

    readonly sessionEndedMessage: string;

    readonly isPhone: boolean;

    readonly _domUtilsService: IDomUtilsService;

    readonly goToInvitePage: () => void;

    readonly _sessionType: InvitationEndedSessionTypes = InvitationEndedSessionTypes.regular;

    readonly wtSessionEndedMessages?: string[];

    readonly _isExpired: boolean;

    readonly _brandingData: AccountAssetsType[] = [];

    @observable errorMessage: string | undefined;

    constructor(
        translate: ITranslate,
        domUtilsService: IDomUtilsService,
        sessionState: {
            ended: boolean;
            isExpired: boolean;
            sessionType: InvitationEndedSessionTypes;
            reason: InvitationEndedReason;
        },
        goToInvitePage: () => void,
        brandingData: AccountAssetsType[]
    ) {
        this._translate = translate;
        this._domUtilsService = domUtilsService;
        this.goToInvitePage = goToInvitePage;
        this.isSessionEnded = sessionState.ended;
        this._sessionType = sessionState.sessionType;
        this._isExpired = !!sessionState.isExpired;
        this.isPhone = getRootStore().environmentService.isPhone();
        this.sessionEndedMessage = this.isExpired
            ? this._translate('REACT.OBSERVER_STATE.SESSION_EXPIRED')
            : this._translate('REACT.OBSERVER_STATE.THANK_YOU');
        this._brandingData = brandingData;

        if (sessionState.reason) {
            this.sessionEndedMessage = this._getEndMessageFromReasonParam(sessionState.reason);
        }

        this.wtSessionEndedMessages =
            this.isExpired && this._sessionType === InvitationEndedSessionTypes.warmTransfer
                ? [
                      this._translate('REACT.INVITATION_ENDED.MEETING_NOT_AVAILABLE'),
                      this._translate('REACT.INVITATION_ENDED.THANK_YOU')
                  ]
                : undefined;
    }

    @computed
    get isExpired() {
        return this._isExpired;
    }

    @computed
    get sessionType() {
        return this._sessionType;
    }

    @action
    updateErrorMessage(message: string, fieldName: string) {
        this.errorMessage = message ? this.translate(message, {field: fieldName}) : '';
    }

    @computed
    get logoImage() {
        const logoAsset = this._brandingData.find((asset) => asset.name === LOGO_URL_ASSET_NAME);

        return logoAsset?.url || (this.warmTransferExpired ? resolveLogoUrl('') : this.loadImage('logo-guest.png'));
    }

    @computed
    get warmTransferExpired() {
        return this.sessionType === InvitationEndedSessionTypes.warmTransfer && this.isExpired;
    }

    hasBrandingLogo() {
        return !!this._brandingData.find((asset) => asset.name === LOGO_URL_ASSET_NAME);
    }

    loadImage(img: string) {
        return this._domUtilsService.loadImage(img);
    }

    get translate() {
        return this._translate;
    }

    _getEndMessageFromReasonParam(reason: InvitationEndedReason) {
        switch (reason) {
            case InvitationEndedReason.sessionExpired:
                return this._translate('REACT.OBSERVER_STATE.SESSION_EXPIRED');
            case InvitationEndedReason.meetingNotAvailable:
                return this._translate('REACT.INVITATION_ENDED.MEETING_NOT_AVAILABLE');
            case InvitationEndedReason.guestLimitExceeded:
                return this._translate('REACT.OBSERVER_STATE.GUEST_LIMIT_EXCEEDED');
            case InvitationEndedReason.removedByAgent:
                return this._translate('REACT.NWAY.INVITATION_ENDED.REMOVED_BY_AGENT');
            case InvitationEndedReason.requestDenied:
                return this._translate('REACT.NWAY.INVITATION_ENDED.REQUEST_DENIED');
            default:
                return this._translate('REACT.OBSERVER_STATE.THANK_YOU');
        }
    }
}
